/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useLocationHistoryStack } from "../../../../hooks/locationHistoryStack";
import { handleUpload } from "../../../../utils/handleUpload";
import { createReason, listReasons, showReason, updateReason } from "@/services";
import { createFile } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: any = {
  id: "",
  name: "",
  idOccurrenceType: null,
  entityType: "twm_occurrences",
  canBeAddedToSite: false,
  editableActivityFields: [],
  siteCriticalAccess: false,
  createRefundEntryIfPaid: false,
  removeMeasument: false,
};

export const useReasons = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
  typeDefault,
  id: idSelect,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
  id?: string;
  typeDefault?: string;
}) => {
  const { idReason: id = idSelect || "new", idActivity } = useParams();
  const { previous } = useLocationHistoryStack();
  const [reasons, setReasons] = useState<any[]>([]);
  const [reason, setReason] = useState<any>(DEFAULT_DATA);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [disableType, setDisableType] = useState<boolean>(false);
  const navigate = useNavigate();
  const pathNew = `/towermanagement/reasons/new`;
  const pathBack = `/towermanagement/reasons`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleReason = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setReason((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      if (key === "type") newData["idReason"] = null;
      return { ...curr, ...newData };
    });
  };

  const goEdit = (id: string | number) => {
    const path = `/towermanagement/reasons/${id}`;
    navigate(path);
  };

  const goBack = () => {
    if (previous?.pathname.includes(pathNew)) {
      navigate(pathBack);
      return;
    }
    navigate(-1);
  };

  const getAll = ({
    page = 1,
    limit,
    filter: filterParam,
  }: {
    page?: number;
    limit?: number;
    filter?: Record<string, any>;
  }) => {
    setLoading(true);
    listReasons({
      includes: [...includes],
      filter: { ...filter, ...filterParam },
      page,
      limit,
      sort,
    })
      .then(
        ({ reasons: listReasons, limit, currentPage, totalItems }) => {
          setReasons(listReasons);
          
          setLimit(Number(limit));
          setTotalItems(Number(totalItems));
        }
      )
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listReasons, key: "reasons", includes, filter });



  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showReason({ id, includes: ['activityFiles'] })
      .then(({ reason: reasonsShow }) => {
        setReason(reasonsShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createReason({ reason })
      .then(({ reason: reasonCreate }) => {
        toast.success(`Motivo criado com sucesso!`);
        goEdit(reasonCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const createFiles = (fileUpload: any, onProgress: (data: number) => void) => {
    setLoading(true);
    createFile({ file: fileUpload })
      .then(({ file: fileCreate }) => {
        const value = [...(reason.activityFiles || []), { file: fileCreate}]
        handleReason('activityFiles', value)
        handleUpload({
          dataUpload: { urlUpload: fileCreate.urlUpload, ...fileUpload },
          onProgress
        }).then(async () => {
          const value = [...reason.idsFile, fileCreate.id]
          handleReason('idsFile', value)
        });
      })
      .finally(() => setLoading(false));
  };


  const update = async (onUpdate?: (d: any) => void) => {
    setLoading(true);
    await updateReason({
      id: reason.id!,
      reason,
    })
      .then(({ reason: reasonUpdate }) => {
        toast.success(`Motivo atualizado com sucesso!`);
        if (onUpdate) {
          onUpdate(reasonUpdate);
          return;
        }
        goEdit(reasonUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  const goAdd = () => {
    if (idActivity) {
      navigate(`/towermanagement/activities/${idActivity}/reasons/new`) 
      return;
    }
    navigate(pathNew);
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    if (typeDefault) {
      handleReason("type", typeDefault);
      setDisableType(true);
    }
  }, [typeDefault]);

  useEffect(() => {
    if (id === "new") handleReason("idActivity", idActivity);
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  return {
    disableType,
    reasons,
    reason,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleReason,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    handleSort,
    createFiles
  };
};
